import React, { useMemo } from 'react';
import { ClockIcon } from '@heroicons/react/24/solid';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import {
  ensureEventos,
  formatEventoTipos,
  DOCUMENTO_TIPO_LABELS,
  formatCambioValor,
} from '../utils/fianzaEventos';

const EVENT_FILE_LABELS = {
  fianzaPdf: 'PDF fianza',
  fianzaXml: 'XML fianza',
  facturaPdf: 'PDF factura',
  facturaXml: 'XML factura',
  comprobantePago: 'Comprobante de pago',
  endosoPdf: 'PDF endoso',
  documento: 'Documento',
};

async function openStoragePath(path) {
  if (!path) return;
  try {
    const url = await getDownloadURL(ref(storage, path));
    window.open(url, '_blank', 'noopener');
  } catch (e) {
    console.error('No se pudo abrir el archivo', e);
  }
}

/**
 * ContratoTimeline — unified, chronological timeline for a contrato: merges every
 * fianza's eventos[] (emisión, endosos, cancelación, anulación) with the
 * contract's attached documentos[]. Data-driven (not sourced from bitácora).
 */
export function ContratoTimeline({ contratoData }) {
  const items = useMemo(() => {
    const out = [];
    (contratoData?.fianzas || []).forEach((f) => {
      const numero = f.numeroFianza || 'sin número';
      ensureEventos(f).forEach((ev) => {
        out.push({
          key: `${f.id}_${ev.id}`,
          kind: 'evento',
          when: ev.fecha || ev.createdAt || 0,
          fianzaNumero: numero,
          tipo: ev.tipo,
          label: formatEventoTipos(ev),
          cambios: ev.cambios || [],
          nota: ev.nota || null,
          filePaths: ev.filePaths || {},
        });
      });
    });
    (contratoData?.documentos || []).forEach((d) => {
      out.push({
        key: `doc_${d.id}`,
        kind: 'documento',
        when: d.uploadedAt || 0,
        label: DOCUMENTO_TIPO_LABELS[d.tipo] || d.tipo || 'Documento',
        nombre: d.nombre || null,
        nota: d.nota || null,
        storagePath: d.storagePath || null,
      });
    });
    // Legacy data often re-uploaded a fianza as a separate array entry instead of
    // an endoso, so the same número shows several synthesized "emisión" events.
    // Only the earliest (per real número) is the emisión; relabel the rest as
    // "Actualización".
    const earliestEmision = {};
    out.forEach((it) => {
      if (it.kind === 'evento' && it.tipo === 'emision' && it.fianzaNumero && it.fianzaNumero !== 'sin número') {
        const k = it.fianzaNumero;
        if (earliestEmision[k] == null || it.when < earliestEmision[k]) earliestEmision[k] = it.when;
      }
    });
    out.forEach((it) => {
      if (it.kind === 'evento' && it.tipo === 'emision' &&
          it.fianzaNumero && it.fianzaNumero !== 'sin número' &&
          earliestEmision[it.fianzaNumero] != null && it.when > earliestEmision[it.fianzaNumero]) {
        it.tipo = 'actualizacion';
        it.label = 'Actualización';
      }
    });

    return out.sort((a, b) => (b.when || 0) - (a.when || 0));
  }, [contratoData]);

  return (
    <div className="mt-4 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2 mb-3">
        <ClockIcon className="w-4 h-4 text-azul-surelia" />
        Historial del contrato
      </h3>

      {items.length === 0 ? (
        <div className="text-xs text-gray-500">Sin movimientos registrados.</div>
      ) : (
        <ol className="relative border-l border-gray-200 ml-2 space-y-4">
          {items.map((it) => (
            <li key={it.key} className="ml-4">
              <div className={`absolute -left-1.5 mt-1 w-3 h-3 rounded-full border-2 border-white ${it.kind === 'documento' ? 'bg-cian-digital' : 'bg-azul-surelia'}`} />
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-gray-800">
                  {it.label}
                  {it.kind === 'evento' && (
                    <span className="ml-2 text-xs font-normal text-gray-400">Fianza #{it.fianzaNumero}</span>
                  )}
                  {it.kind === 'documento' && it.nombre && (
                    <span className="ml-2 text-xs font-normal text-gray-400">{it.nombre}</span>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {it.when ? new Date(it.when).toLocaleDateString('es-MX') : ''}
                </div>
              </div>

              {it.kind === 'evento' && it.cambios.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {it.cambios.map((c) => (
                    <li key={c.campo} className="text-xs text-gray-600 flex flex-wrap items-center gap-1">
                      <span className="font-medium">{c.label}:</span>
                      <span className="text-gray-400 line-through">{formatCambioValor(c.campo, c.valorAnterior)}</span>
                      <span className="text-gray-300">→</span>
                      <span className="text-gray-800">{formatCambioValor(c.campo, c.valorNuevo)}</span>
                    </li>
                  ))}
                </ul>
              )}

              {it.nota && <div className="mt-1 text-xs text-gray-500 italic">{it.nota}</div>}

              <div className="mt-1 flex flex-wrap gap-2">
                {it.kind === 'evento' &&
                  Object.entries(it.filePaths).map(([k, path]) => (
                    <button key={k} type="button" onClick={() => openStoragePath(path)} className="text-xs text-cian-digital hover:underline">
                      {EVENT_FILE_LABELS[k] || k}
                    </button>
                  ))}
                {it.kind === 'documento' && it.storagePath && (
                  <button type="button" onClick={() => openStoragePath(it.storagePath)} className="text-xs text-cian-digital hover:underline">
                    Ver documento
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default ContratoTimeline;
